import { Component, Input, OnDestroy, effect, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
    AlertBannerComponent,
    PasswordResetNotificationService,
    PasswordResetService,
    passwordResetConfirmationText,
    passwordResetWarningMessage,
    passwordResetMessageList,
    resolvePasswordResetErrorMessage
} from 'lib';

@Component({
    selector: 'mmoga-password-reset-modal',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, AlertBannerComponent],
    templateUrl: './password-reset-modal.component.html',
    styleUrl: './password-reset-modal.component.scss'
})
export class PasswordResetModalComponent implements OnDestroy {
    private activeModal = inject(NgbActiveModal);
    private passwordResetService = inject(PasswordResetService);
    private fb = inject(FormBuilder);

    private readonly notificationService = inject(PasswordResetNotificationService);

    @Input() enterpriseIds: number[] = [];
    @Input() totalUserCount = 0;

    readonly notification = this.notificationService.notification;
    readonly isProcessing = this.notificationService.isProcessing;
    readonly confirmationWord = passwordResetConfirmationText;
    readonly warningMessage = passwordResetWarningMessage;
    readonly modalMessageList = passwordResetMessageList;

    confirmationForm: FormGroup;
    isResetInProgress = false;
    private httpError = false;
    private httpErrorMessage = '';

    private destroy$ = new Subject<void>();

    constructor() {
        this.confirmationForm = this.fb.group({
            confirmationText: ['', [Validators.required, this.confirmationValidator()]]
        });

        this.notificationService.reset();

        effect(() => {
            if (this.isProcessing()) {
                this.confirmationForm.get('confirmationText')?.disable();
            } else if (!this.isResetInProgress) {
                this.confirmationForm.get('confirmationText')?.enable();
            }
        });
    }

    get confirmationText(): AbstractControl | null {
        return this.confirmationForm.get('confirmationText');
    }

    get hasNotification(): boolean {
        return this.notification().type !== null || this.httpError;
    }

    get notificationAlertType(): string {
        if (this.notification().type !== null) {
            return this.notification().alertType ?? '';
        }

        return this.httpError ? 'error' : '';
    }

    get notificationMessage(): string {
        if (this.notification().type !== null) {
            return this.notification().message || '';
        }

        return this.httpError ? this.httpErrorMessage : '';
    }

    get isResetEnabled(): boolean {
        return this.confirmationForm.valid && !this.isResetInProgress && !this.isProcessing();
    }

    get isResetPasswordProcessing(): boolean {
        return this.isResetInProgress || this.isProcessing();
    }

    accept(): void {
        if (!this.isResetEnabled) {
            return;
        }

        this.notificationService.subscribeToGlobalPasswordResetNotifications();
        this.notificationService.reset();
        this.isResetInProgress = true;
        this.httpError = false;
        this.httpErrorMessage = '';
        this.confirmationForm.get('confirmationText')?.disable();

        this.passwordResetService.triggerPasswordResetBatch(this.enterpriseIds)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isResetInProgress = false;
                    if (!this.isProcessing()) {
                        this.confirmationForm.get('confirmationText')?.enable();
                    }
                    this.confirmationForm.reset();
                })
            )
            .subscribe({
                error: (err) => {
                    this.httpError = true;
                    this.httpErrorMessage = resolvePasswordResetErrorMessage(err);
                }
            });
    }

    decline(): void {
        this.activeModal.close(false);
    }

    dismiss(): void {
        this.activeModal.dismiss();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private confirmationValidator(): ValidatorFn {
        return (control: AbstractControl): Record<string, boolean> | null => {
            if (control.value && control.value !== this.confirmationWord) {
                return { confirmationMismatch: true };
            }
            return null;
        };
    }
}
